/**
 * 예제 페이지가 공유하는 타입과 URL 빌더.
 *
 * 인증 게이트는 ProxyMiddleware가 path 패턴(/tiles, /data/:id.json)에서만 작동하므로,
 * read-token 모드는 ?token=<jwt> 쿼리스트링으로 붙이면 admin 로그인과 무관하게 동작한다.
 * public 모드는 토큰 없이 호출 — mbtiles가 private이면 401이 그대로 노출되어
 * "권한 분리가 의도대로 동작함" 을 사용자가 직접 확인할 수 있다.
 */

export type AccessMode = 'public' | 'token';

export interface TileJsonResponse {
  tilejson?: string;
  name?: string;
  format?: string;
  tiles: string[];
  minzoom?: number;
  maxzoom?: number;
  bounds?: [number, number, number, number];
  center?: [number, number, number?];
  vector_layers?: Array<{
    id: string;
    fields?: Record<string, string>;
    minzoom?: number;
    maxzoom?: number;
  }>;
}

export type TileFormat = 'raster' | 'vector';

export function classifyFormat(format: string | undefined): TileFormat {
  // tileserver-gl이 raster mbtiles에 대해 png/jpg/webp 중 하나를, vector에 대해 pbf를 돌려준다.
  return format === 'pbf' || format === 'mvt' ? 'vector' : 'raster';
}

export function buildTileJsonUrl(
  baseUrl: string,
  id: string,
  mode: AccessMode,
  token: string,
): string {
  const base = `${trimTrailingSlash(baseUrl)}/data/${encodeURIComponent(id)}.json`;
  if (mode === 'token' && token) {
    return `${base}?token=${encodeURIComponent(token)}`;
  }
  return base;
}

/**
 * tiles[] URL 이 path-only(`/tiles/...`) 면 baseUrl 을 앞에 붙여 절대 URL 로 만든다.
 * 이미 절대 URL(MBTILER_PUBLIC_BASE_URL 설정 시) 이면 그대로 둔다.
 */
export function resolveTileUrl(baseUrl: string, tileUrl: string): string {
  if (/^https?:\/\//i.test(tileUrl)) return tileUrl;
  return `${trimTrailingSlash(baseUrl)}${tileUrl.startsWith('/') ? '' : '/'}${tileUrl}`;
}

/**
 * read-token 모드에선 tiles[] 에 ?token= 가 붙어있지 않으므로 클라이언트에서 추가한다.
 * raster XYZ source / MapLibre raster source 둘 다 그대로 쓰기 좋도록 첫 번째 tiles[0]
 * 템플릿에 토큰 쿼리스트링을 합쳐 돌려준다.
 */
export function withTokenQuery(tileUrl: string, token: string): string {
  if (!token) return tileUrl;
  const sep = tileUrl.includes('?') ? '&' : '?';
  return `${tileUrl}${sep}token=${encodeURIComponent(token)}`;
}

function trimTrailingSlash(s: string): string {
  return s.replace(/\/+$/, '');
}

/** 사용자가 baseUrl 입력 칸을 비워뒀을 때 쓰는 기본값. SSR 환경에선 빈 문자열. */
export function defaultBaseUrl(): string {
  return typeof window !== 'undefined' ? window.location.origin : '';
}

export async function fetchTileJson(url: string): Promise<TileJsonResponse> {
  const res = await fetch(url);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`TileJSON ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as TileJsonResponse;
}
